import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createParticipant, getParticipantById, updateParticipant } from '../../services/participantService';
import { getHouses } from '../../services/houseService';
import { getDepartments } from '../../services/departmentService';
import { getEnrollmentsForStudent, updateEnrollment } from '../../services/studentEnrollmentService';
import { getFamilies, createFamily, assignStudentToFamily, getFamilyPayments, upsertFamilyPayment } from '../../services/familyService';
import { getActiveSportsYear, getSportsYears } from '../../services/sportsYearService';
import { supabase } from '../../lib/supabase';
import { Participant, House, Department, StudentEnrollment, Family, FamilyPayment, FamilyPaymentStatus } from '../../types/models';
import { format } from 'date-fns';
import { ArrowLeft, Save, History, Users, DollarSign, Plus } from 'lucide-react';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const ParticipantForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState<Omit<Participant, 'id' | 'created_at' | 'house'>>({
    first_name: '',
    last_name: '',
    first_name_tamil: '',
    last_name_tamil: '',
    date_of_birth: format(new Date(), 'yyyy-MM-dd'),
    gender: '',
    house_id: '',
    department_id: '',
    reference_number: '',
    payment_status: 'not_paid',
    payment_date: '',
    payment_notes: ''
  });
  
  const [participant, setParticipant] = useState<Participant | null>(null);
  const [houses, setHouses] = useState<House[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(isEditMode);

  const [enrollments, setEnrollments] = useState<StudentEnrollment[]>([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);
  const [enrollmentSaving, setEnrollmentSaving] = useState<string | null>(null);
  const [enrollmentMessage, setEnrollmentMessage] = useState<string | null>(null);

  const [families, setFamilies] = useState<Family[]>([]);
  const [currentFamilyId, setCurrentFamilyId] = useState<string | null>(null);
  const [familySaving, setFamilySaving] = useState(false);
  const [familyMessage, setFamilyMessage] = useState<string | null>(null);
  const [familySearch, setFamilySearch] = useState('');
  const [familyPickerOpen, setFamilyPickerOpen] = useState(false);

  const [payments, setPayments] = useState<FamilyPayment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [activeYear, setActiveYear] = useState<number | null>(null);
  const [allSportsYears, setAllSportsYears] = useState<number[]>([]);
  const [paymentDraft, setPaymentDraft] = useState<{
    year: string;
    status: FamilyPaymentStatus;
    paid_at: string;
    amount: string;
    notes: string;
  } | null>(null);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isEditMode) return;
    Promise.all([getActiveSportsYear().catch(() => null), getSportsYears().catch(() => [])])
      .then(([active, years]) => {
        setActiveYear(active);
        setAllSportsYears(years.map((y) => y.year).sort((a, b) => b - a));
      });
  }, [isEditMode]);

  const loadPayments = async (familyId: string | null) => {
    if (!familyId) {
      setPayments([]);
      return;
    }
    setPaymentsLoading(true);
    try {
      const rows = await getFamilyPayments(familyId);
      setPayments(rows);
    } catch (err) {
      console.error('Error loading family payments:', err);
    } finally {
      setPaymentsLoading(false);
    }
  };

  useEffect(() => {
    if (isEditMode) loadPayments(currentFamilyId);
  }, [currentFamilyId, isEditMode]);

  const openPaymentDraft = (
    year: number,
    existing?: FamilyPayment
  ) => {
    setPaymentMessage(null);
    setPaymentDraft({
      year: year.toString(),
      status: existing?.status ?? 'not_paid',
      paid_at: existing?.paid_at ?? '',
      amount: existing?.amount?.toString() ?? '',
      notes: existing?.notes ?? '',
    });
  };

  const handlePaymentSave = async () => {
    if (!paymentDraft || !currentFamilyId) return;
    const year = parseInt(paymentDraft.year, 10);
    if (!year) {
      setPaymentMessage('Year is required');
      return;
    }
    try {
      setPaymentSaving(true);
      await upsertFamilyPayment({
        family_id: currentFamilyId,
        competition_year: year,
        status: paymentDraft.status,
        paid_at: paymentDraft.paid_at || null,
        amount: paymentDraft.amount ? parseFloat(paymentDraft.amount) : null,
        notes: paymentDraft.notes || null,
      });
      await loadPayments(currentFamilyId);
      setPaymentDraft(null);
      setPaymentMessage(`Payment for ${year} saved`);
    } catch (err: any) {
      setPaymentMessage(err.message || 'Failed to save payment');
    } finally {
      setPaymentSaving(false);
    }
  };

  useEffect(() => {
    if (!isEditMode) return;
    getFamilies()
      .then(setFamilies)
      .catch((err) => console.error('Error loading families:', err));
  }, [isEditMode]);

  useEffect(() => {
    if (!isEditMode || !id || !UUID_REGEX.test(id)) return;
    supabase
      .from('students')
      .select('family_id')
      .eq('student_id', id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setCurrentFamilyId(data.family_id || null);
      });
  }, [id, isEditMode]);

  const handleAssignFamily = async (newFamilyId: string | null) => {
    if (!id) return;
    setFamilyMessage(null);
    try {
      setFamilySaving(true);
      await assignStudentToFamily(id, newFamilyId);
      setCurrentFamilyId(newFamilyId);
      setFamilyMessage('Family updated');
    } catch (err: any) {
      setFamilyMessage(err.message || 'Failed to update family');
    } finally {
      setFamilySaving(false);
    }
  };

  const handleCreateFamilyForStudent = async () => {
    if (!id) return;
    const lastName = formData.last_name || 'New family';
    setFamilyMessage(null);
    try {
      setFamilySaving(true);
      const newFamily = await createFamily({
        family_name: lastName,
        department_id: formData.department_id || null,
      });
      await assignStudentToFamily(id, newFamily.family_id);
      const refreshed = await getFamilies();
      setFamilies(refreshed);
      setCurrentFamilyId(newFamily.family_id);
      setFamilyMessage(`Created new "${lastName}" family and assigned this student`);
    } catch (err: any) {
      setFamilyMessage(err.message || 'Failed to create family');
    } finally {
      setFamilySaving(false);
    }
  };

  useEffect(() => {
    if (!isEditMode || !id || !UUID_REGEX.test(id)) return;
    setEnrollmentsLoading(true);
    getEnrollmentsForStudent(id)
      .then(setEnrollments)
      .catch((err) => console.error('Error loading enrollments:', err))
      .finally(() => setEnrollmentsLoading(false));
  }, [id, isEditMode]);

  const handleEnrollmentChange = (
    enrollmentId: string,
    field: 'department_id' | 'house_id' | 'active',
    value: string | boolean | null
  ) => {
    setEnrollments((prev) =>
      prev.map((e) =>
        e.enrollment_id === enrollmentId ? { ...e, [field]: value } : e
      )
    );
  };

  const handleEnrollmentSave = async (enrollment: StudentEnrollment) => {
    setEnrollmentMessage(null);
    try {
      setEnrollmentSaving(enrollment.enrollment_id);
      const updated = await updateEnrollment(enrollment.enrollment_id, {
        department_id: enrollment.department_id,
        house_id: enrollment.house_id,
        active: enrollment.active,
      });
      setEnrollments((prev) =>
        prev.map((e) => (e.enrollment_id === enrollment.enrollment_id ? updated : e))
      );
      setEnrollmentMessage(`Saved enrollment for ${enrollment.competition_year}`);
    } catch (err: any) {
      console.error('Error saving enrollment:', err);
      setEnrollmentMessage(err.message || 'Failed to save enrollment');
    } finally {
      setEnrollmentSaving(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [housesData, departmentsData] = await Promise.all([
          getHouses(),
          getDepartments()
        ]);
        setHouses(housesData);
        setDepartments(departmentsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data');
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchParticipant = async () => {
      if (id) {
        if (!UUID_REGEX.test(id)) {
          setError('Invalid participant ID');
          setFormLoading(false);
          return;
        }

        try {
          setFormLoading(true);
          const data = await getParticipantById(id);
          if (data) {
            setParticipant(data);
            setFormData({
              first_name: data.first_name,
              last_name: data.last_name,
              first_name_tamil: data.first_name_tamil || '',
              last_name_tamil: data.last_name_tamil || '',
              date_of_birth: format(new Date(data.date_of_birth), 'yyyy-MM-dd'),
              gender: data.gender,
              house_id: data.house_id,
              department_id: data.department_id || '',
              reference_number: data.reference_number || '',
              payment_status: data.payment_status || 'not_paid',
              payment_date: data.payment_date ? format(new Date(data.payment_date), 'yyyy-MM-dd') : '',
              payment_notes: data.payment_notes || ''
            });
          } else {
            setError('Participant not found');
          }
        } catch (error: any) {
          console.error('Error fetching participant:', error);
          setError(error.message || 'Failed to load participant data');
        } finally {
          setFormLoading(false);
        }
      }
    };

    if (isEditMode) {
      fetchParticipant();
    }
  }, [id, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      // Create a copy of the form data to modify before sending
      const submissionData = {
        ...formData,
        // Set payment_date to null if it's empty
        payment_date: formData.payment_date ? formData.payment_date : null
      };
      
      if (isEditMode && id) {
        if (!UUID_REGEX.test(id)) {
          throw new Error('Invalid participant ID');
        }
        await updateParticipant(id, submissionData);
      } else {
        await createParticipant(submissionData);
      }
      
      navigate('/participants');
    } catch (error: any) {
      console.error('Error saving participant:', error);
      setError(error.message || 'Failed to save participant');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'registered':
        return 'bg-blue-100 text-blue-800';
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'withdrawn':
        return 'bg-red-100 text-red-800';
      case 'disqualified':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (formLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (isEditMode && id && !UUID_REGEX.test(id)) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-red-600 font-medium">Invalid participant ID</div>
        <button
          onClick={() => navigate('/participants')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          {t('participants.actions.cancel')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? t('participants.form.editTitle') : t('participants.form.addTitle')}
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                {t('participants.form.fields.firstName.label')}
              </label>
              <input
                type="text"
                name="first_name"
                id="first_name"
                required
                value={formData.first_name}
                onChange={handleChange}
                placeholder={t('participants.form.fields.firstName.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                {t('participants.form.fields.lastName.label')}
              </label>
              <input
                type="text"
                name="last_name"
                id="last_name"
                required
                value={formData.last_name}
                onChange={handleChange}
                placeholder={t('participants.form.fields.lastName.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="first_name_tamil" className="block text-sm font-medium text-gray-700">
                {t('participants.form.fields.firstNameTamil.label')}
              </label>
              <input
                type="text"
                name="first_name_tamil"
                id="first_name_tamil"
                value={formData.first_name_tamil}
                onChange={handleChange}
                placeholder={t('participants.form.fields.firstNameTamil.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="last_name_tamil" className="block text-sm font-medium text-gray-700">
                {t('participants.form.fields.lastNameTamil.label')}
              </label>
              <input
                type="text"
                name="last_name_tamil"
                id="last_name_tamil"
                value={formData.last_name_tamil}
                onChange={handleChange}
                placeholder={t('participants.form.fields.lastNameTamil.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700">
                {t('participants.form.fields.dateOfBirth.label')}
              </label>
              <input
                type="date"
                name="date_of_birth"
                id="date_of_birth"
                required
                value={formData.date_of_birth}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                {t('participants.form.fields.gender.label')}
              </label>
              <select
                id="gender"
                name="gender"
                required
                value={formData.gender}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="" disabled>{t('participants.form.fields.gender.placeholder')}</option>
                <option value="M">{t('participants.filters.male')}</option>
                <option value="F">{t('participants.filters.female')}</option>
              </select>
            </div>

            <div>
              <label htmlFor="house_id" className="block text-sm font-medium text-gray-700">
                {t('participants.form.fields.house.label')}
              </label>
              <select
                id="house_id"
                name="house_id"
                required
                value={formData.house_id}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="" disabled>{t('participants.form.fields.house.placeholder')}</option>
                {houses.map((house) => (
                  <option key={house.house_id} value={house.house_id}>
                    {house.house_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="department_id" className="block text-sm font-medium text-gray-700">
                {t('participants.form.fields.department.label')}
              </label>
              <select
                id="department_id"
                name="department_id"
                value={formData.department_id}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500  sm:text-sm"
              >
                <option value="">{t('participants.form.fields.department.placeholder')}</option>
                {departments.map((department) => (
                  <option key={department.department_id} value={department.department_id}>
                    {department.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="reference_number" className="block text-sm font-medium text-gray-700">
                {t('participants.form.fields.referenceNumber.label')}
              </label>
              <input
                type="text"
                name="reference_number"
                id="reference_number"
                value={formData.reference_number}
                onChange={handleChange}
                placeholder={t('participants.form.fields.referenceNumber.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          {participant?.registrations && participant.registrations.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('participants.form.registrations.title')}
              </h3>
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('participants.form.registrations.table.event')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('participants.form.registrations.table.category')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('participants.form.registrations.table.year')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('participants.form.registrations.table.status')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('participants.form.registrations.table.eventStatus')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {participant.registrations.map((registration) => (
                      <tr key={registration.registration_id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {registration.event?.event_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {registration.event?.category?.category_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {registration.competition_year}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(registration.status)}`}>
                            {t(`participants.form.registrations.status.${registration.status}`)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            registration.event?.status === 'not_started' ? 'bg-gray-100 text-gray-800' :
                            registration.event?.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {t(`participants.form.registrations.eventStatus.${registration.event?.status}`)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/participants')}
              className="mr-3 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('participants.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? t('participants.actions.saving') : t('participants.actions.save')}
            </button>
          </div>
        </form>
      </div>

      {isEditMode && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
              <Users className="h-5 w-5 mr-2 text-blue-500" />
              Family
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Which family this student belongs to. Family-level payments and contact information
              are managed on the Family Connections page.
            </p>
          </div>
          <div className="p-4 space-y-3">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Assigned family</label>
              {(() => {
                const current = families.find((f) => f.family_id === currentFamilyId);
                const needle = familySearch.toLowerCase();
                const matches = !needle
                  ? families.slice(0, 25)
                  : families.filter((f) => (
                      f.family_name.toLowerCase().includes(needle) ||
                      (f.father_name || '').toLowerCase().includes(needle) ||
                      (f.mother_name || '').toLowerCase().includes(needle) ||
                      (f.department?.name || '').toLowerCase().includes(needle)
                    )).slice(0, 50);
                return (
                  <>
                    <div className="border border-gray-300 rounded-md bg-white">
                      <div className="px-3 py-2 flex items-center justify-between border-b">
                        <div className="text-sm">
                          {current ? (
                            <>
                              <span className="font-medium text-gray-900">{current.family_name}</span>
                              <span className="text-gray-500">
                                {current.father_name && <> · Father: {current.father_name}</>}
                                {current.mother_name && <> · Mother: {current.mother_name}</>}
                                {current.member_count ? ` · ${current.member_count} members` : ''}
                              </span>
                            </>
                          ) : (
                            <span className="text-gray-500">No family assigned</span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {current && (
                            <button
                              type="button"
                              onClick={() => handleAssignFamily(null)}
                              disabled={familySaving}
                              className="text-xs text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setFamilyPickerOpen((v) => !v)}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            {familyPickerOpen ? 'Close' : 'Change'}
                          </button>
                        </div>
                      </div>
                      {familyPickerOpen && (
                        <div>
                          <input
                            type="text"
                            value={familySearch}
                            onChange={(e) => setFamilySearch(e.target.value)}
                            placeholder="Search by family name, parent or department"
                            className="block w-full border-0 border-b border-gray-200 py-2 px-3 text-sm focus:ring-0 focus:border-blue-500"
                          />
                          <div className="max-h-64 overflow-y-auto divide-y">
                            {matches.length === 0 && (
                              <div className="p-3 text-sm text-gray-500">No families match</div>
                            )}
                            {matches.map((f) => (
                              <button
                                key={f.family_id}
                                type="button"
                                onClick={() => {
                                  handleAssignFamily(f.family_id);
                                  setFamilyPickerOpen(false);
                                  setFamilySearch('');
                                }}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${
                                  f.family_id === currentFamilyId ? 'bg-blue-100' : ''
                                }`}
                              >
                                <div className="font-medium text-gray-900">
                                  {f.family_name}
                                  {f.member_count ? ` · ${f.member_count} members` : ''}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {f.father_name && <>Father: {f.father_name} </>}
                                  {f.mother_name && <>· Mother: {f.mother_name} </>}
                                  {f.department?.name && <>· {f.department.name}</>}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={handleCreateFamilyForStudent}
                        disabled={familySaving}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        <Users className="h-4 w-4 mr-1" />
                        Create family from last name
                      </button>
                      <Link
                        to="/participants/family-connections"
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Manage families
                      </Link>
                    </div>
                  </>
                );
              })()}
            </div>
            {familyMessage && (
              <p className="text-sm text-gray-700">{familyMessage}</p>
            )}
          </div>
        </div>
      )}

      {isEditMode && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex items-start justify-between gap-2">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                Payments per year
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Payments are tracked at the family level per sports year. All siblings inherit the
                same status. {currentFamilyId ? '' : 'Assign a family above to record payments.'}
              </p>
            </div>
            {currentFamilyId && activeYear && !payments.some((p) => p.competition_year === activeYear) && (
              <button
                type="button"
                onClick={() => openPaymentDraft(activeYear)}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Record for {activeYear}
              </button>
            )}
          </div>
          <div className="p-4 space-y-3">
            {!currentFamilyId ? (
              <p className="text-sm text-gray-500 text-center py-4">
                This student is not linked to a family yet.
              </p>
            ) : paymentsLoading ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <>
                {payments.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No payment records yet for this family.
                  </p>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Year</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {payments.map((p) => (
                        <tr key={p.payment_id} className={p.competition_year === activeYear ? 'bg-blue-50/40' : ''}>
                          <td className="px-3 py-2 text-sm font-medium text-gray-900">{p.competition_year}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              p.status === 'paid' ? 'bg-green-100 text-green-800' :
                              p.status === 'school_paid' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {p.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-700">{p.paid_at || '-'}</td>
                          <td className="px-3 py-2 text-sm text-gray-700">{p.amount ?? '-'}</td>
                          <td className="px-3 py-2 text-sm text-gray-700 max-w-xs truncate" title={p.notes ?? ''}>{p.notes || '-'}</td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => openPaymentDraft(p.competition_year, p)}
                              className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {paymentDraft && (
                  <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                    <div className="text-sm font-medium text-gray-900">
                      Payment for year {paymentDraft.year}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Year</label>
                        <select
                          value={paymentDraft.year}
                          onChange={(e) => setPaymentDraft({ ...paymentDraft, year: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm"
                        >
                          {allSportsYears.length === 0 && (
                            <option value={paymentDraft.year}>{paymentDraft.year}</option>
                          )}
                          {allSportsYears.map((y) => (
                            <option key={y} value={y.toString()}>{y}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Status</label>
                        <select
                          value={paymentDraft.status}
                          onChange={(e) => setPaymentDraft({ ...paymentDraft, status: e.target.value as FamilyPaymentStatus })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm"
                        >
                          <option value="not_paid">not_paid</option>
                          <option value="paid">paid</option>
                          <option value="school_paid">school_paid</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Date</label>
                        <input
                          type="date"
                          value={paymentDraft.paid_at}
                          onChange={(e) => setPaymentDraft({ ...paymentDraft, paid_at: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={paymentDraft.amount}
                          onChange={(e) => setPaymentDraft({ ...paymentDraft, amount: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm"
                        />
                      </div>
                      <div className="col-span-2 md:col-span-4">
                        <label className="block text-xs font-medium text-gray-700">Notes</label>
                        <textarea
                          rows={2}
                          value={paymentDraft.notes}
                          onChange={(e) => setPaymentDraft({ ...paymentDraft, notes: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setPaymentDraft(null)}
                        className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handlePaymentSave}
                        disabled={paymentSaving}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
                      >
                        <Save className="h-3 w-3 mr-1" />
                        {paymentSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                )}
                {paymentMessage && (
                  <p className="text-sm text-gray-700">{paymentMessage}</p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {isEditMode && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
              <History className="h-5 w-5 mr-2 text-indigo-500" />
              Enrollment history
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Per-year house and department for this student. The active year is kept in sync
              automatically when you edit the main form; past years can be corrected below.
            </p>
          </div>
          <div className="p-4">
            {enrollmentsLoading ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : enrollments.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                No enrollment history yet. It will be created automatically when a sports year is activated.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">House</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {enrollments.map((e) => (
                      <tr key={e.enrollment_id}>
                        <td className="px-3 py-2 text-sm font-medium text-gray-900">{e.competition_year}</td>
                        <td className="px-3 py-2">
                          <select
                            value={e.department_id || ''}
                            onChange={(ev) =>
                              handleEnrollmentChange(e.enrollment_id, 'department_id', ev.target.value || null)
                            }
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-1 px-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">-</option>
                            {departments.map((d) => (
                              <option key={d.department_id} value={d.department_id}>
                                {d.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={e.house_id || ''}
                            onChange={(ev) =>
                              handleEnrollmentChange(e.enrollment_id, 'house_id', ev.target.value || null)
                            }
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-1 px-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">-</option>
                            {houses.map((h) => (
                              <option key={h.house_id} value={h.house_id}>
                                {h.house_name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={e.active}
                            onChange={(ev) =>
                              handleEnrollmentChange(e.enrollment_id, 'active', ev.target.checked)
                            }
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => handleEnrollmentSave(e)}
                            disabled={enrollmentSaving === e.enrollment_id}
                            className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
                          >
                            <Save className="h-3 w-3 mr-1" />
                            {enrollmentSaving === e.enrollment_id ? 'Saving...' : 'Save'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {enrollmentMessage && (
                  <p className="mt-3 text-sm text-gray-700">{enrollmentMessage}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticipantForm;